import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

export const AUTH_STORAGE_KEYS = [
    'token',
    'user',
    'auth_session_type',
    'id',
    'user_name',
    'user_email',
    'user_role',
    'adminInfo',
    'is_super_admin',
    'admin_group_id',
    'admin_group_ids',
    'user_group_id',
    'user_group_ids',
    'user_affiliation',
    'user_management_group',
    'hospital_id',
    'hospital_name',
    'hospital_type_code',
    'hospital_type_name',
    'region_code',
    'region_name',
    'district_code',
    'district_name',
    'postal_code',
    'group_role',
    'group_id',
    // 외부 대시보드 SSO 세션이 심는 목록 필터 마커. 로그아웃 시 함께 지워
    // 다음 세션에 필터가 누수되지 않게 한다.
    'external_only_app_user_id',
    'external_only_label'
];

const AuthSessionContext = createContext(null);

const readJson = (value) => {
    if (!value) return null;

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

export const readStoredSession = (storage = window.localStorage) => {
    const token = storage.getItem('token');
    const storedUser = readJson(storage.getItem('user'));
    const fallbackUser = {
        id: storage.getItem('id'),
        name: storage.getItem('user_name'),
        email: storage.getItem('user_email'),
        role: storage.getItem('user_role')
    };
    const hasFallbackUser = Object.values(fallbackUser).some(Boolean);

    return {
        token,
        user: storedUser || (hasFallbackUser ? fallbackUser : null),
        sessionType: storage.getItem('auth_session_type')
    };
};

export const isCenterAdminSession = ({ token, user, sessionType }) => {
    if (!token || !user) return false;

    const role = String(user.role || user.user_role || '').toLowerCase();
    if (role === 'guardian') return false;
    if (sessionType === 'center_admin') return true;

    const email = user.email || user.user_email;
    return Boolean(email);
};

export const clearAuthStorage = (storage = window.localStorage) => {
    AUTH_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
};

const persistValue = (storage, key, value) => {
    if (value === undefined || value === null || value === '') {
        storage.removeItem(key);
        return;
    }

    storage.setItem(key, String(value));
};

export const AuthSessionProvider = ({ children }) => {
    const [session, setSessionState] = useState(() => readStoredSession());

    const setSession = useCallback((sessionOrToken, maybeUser) => {
        const nextToken = typeof sessionOrToken === 'object'
            ? sessionOrToken.token
            : sessionOrToken;
        const nextUser = typeof sessionOrToken === 'object'
            ? sessionOrToken.user
            : maybeUser;

        if (!nextToken || !nextUser) {
            throw new Error('setSession requires both a token and user.');
        }

        localStorage.setItem('token', nextToken);
        localStorage.setItem('user', JSON.stringify(nextUser));
        localStorage.setItem('auth_session_type', 'center_admin');
        persistValue(localStorage, 'id', nextUser.id);
        persistValue(localStorage, 'user_name', nextUser.name || nextUser.user_name);
        persistValue(localStorage, 'user_email', nextUser.email || nextUser.user_email);
        persistValue(localStorage, 'user_role', nextUser.role || nextUser.user_role);

        setSessionState({
            token: nextToken,
            user: nextUser,
            sessionType: 'center_admin'
        });
    }, []);

    const logout = useCallback(() => {
        clearAuthStorage();
        setSessionState({ token: null, user: null, sessionType: null });
    }, []);

    useEffect(() => {
        const syncSession = (event) => {
            if (event.storageArea && event.storageArea !== localStorage) return;
            if (event.key && !AUTH_STORAGE_KEYS.includes(event.key)) return;
            setSessionState(readStoredSession());
        };

        window.addEventListener('storage', syncSession);
        return () => window.removeEventListener('storage', syncSession);
    }, []);

    const value = useMemo(() => ({
        token: session.token,
        user: session.user,
        isCenterAdmin: isCenterAdminSession(session),
        setSession,
        logout
    }), [logout, session, setSession]);

    return (
        <AuthSessionContext.Provider value={value}>
            {children}
        </AuthSessionContext.Provider>
    );
};

export const useAuthSession = () => {
    const context = useContext(AuthSessionContext);
    if (!context) {
        throw new Error('useAuthSession must be used within an AuthSessionProvider');
    }
    return context;
};
